/**
 * SignatureCanvas Component
 *
 * HTML5 Canvas component for capturing digital signatures.
 * Supports both touch and mouse input with smooth drawing.
 *
 * @module pages/technician/signature/SignatureCanvas
 */

import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import './SignatureCapture.css';

const SignatureCanvas = forwardRef(({
  width = 400,
  height = 200,
  lineWidth = 2.5,
  lineColor = '#000000',
  backgroundColor = '#ffffff',
  className = '',
  onChange,
  disabled = false,
}, ref) => {
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width, height });
  const lastPoint = useRef(null);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set up high DPI canvas
    const dpr = window.devicePixelRatio || 1;
    const rect = containerRef.current?.getBoundingClientRect();
    const actualWidth = rect?.width || width;
    const actualHeight = height;

    canvas.width = actualWidth * dpr;
    canvas.height = actualHeight * dpr;
    canvas.style.width = `${actualWidth}px`;
    canvas.style.height = `${actualHeight}px`;

    setCanvasSize({ width: actualWidth, height: actualHeight });

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = lineWidth;

    // Fill background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, actualWidth, actualHeight);

    contextRef.current = ctx;
  }, [width, height, lineWidth, lineColor, backgroundColor]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      // Save current drawing
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.drawImage(canvas, 0, 0);

      // Resize
      const dpr = window.devicePixelRatio || 1;
      const rect = container.getBoundingClientRect();
      const newWidth = rect.width;

      canvas.width = newWidth * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${newWidth}px`;
      canvas.style.height = `${height}px`;

      setCanvasSize({ width: newWidth, height });

      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = lineWidth;

      // Restore background
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, newWidth, height);

      // Restore drawing (scaled)
      if (hasSignature) {
        ctx.drawImage(
          tempCanvas,
          0, 0, tempCanvas.width, tempCanvas.height,
          0, 0, newWidth, height
        );
      }

      contextRef.current = ctx;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [height, lineWidth, lineColor, backgroundColor, hasSignature]);

  // Get coordinates from event
  const getCoordinates = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;

    if (event.touches && event.touches.length > 0) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  // Start drawing
  const startDrawing = (event) => {
    if (disabled) return;

    event.preventDefault();
    const coords = getCoordinates(event);
    if (!coords) return;

    setIsDrawing(true);
    lastPoint.current = coords;

    const ctx = contextRef.current;
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
      // Draw a dot for single taps
      ctx.lineTo(coords.x + 0.1, coords.y + 0.1);
      ctx.stroke();
    }
  };

  // Draw line with smooth interpolation
  const draw = (event) => {
    if (!isDrawing || disabled) return;

    event.preventDefault();
    const coords = getCoordinates(event);
    if (!coords || !lastPoint.current) return;

    const ctx = contextRef.current;
    if (!ctx) return;

    // Quadratic curve for smoother lines
    const midPoint = {
      x: (lastPoint.current.x + coords.x) / 2,
      y: (lastPoint.current.y + coords.y) / 2,
    };

    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.quadraticCurveTo(
      lastPoint.current.x,
      lastPoint.current.y,
      midPoint.x,
      midPoint.y
    );
    ctx.stroke();

    lastPoint.current = coords;

    if (!hasSignature) {
      setHasSignature(true);
      onChange && onChange(true);
    }
  };

  // Stop drawing
  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      lastPoint.current = null;
    }
  };

  // Clear canvas
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = contextRef.current;
    if (!canvas || !ctx) return;

    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);
    setHasSignature(false);
    onChange && onChange(false);
  };

  // Check if canvas is empty
  const isEmpty = () => {
    return !hasSignature;
  };

  // Get canvas as data URL
  const toDataURL = (type = 'image/png', quality = 0.92) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.toDataURL(type, quality);
  };

  // Get canvas as blob
  const toBlob = (callback, type = 'image/png', quality = 0.92) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(callback, type, quality);
  };

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    clear,
    isEmpty,
    toDataURL,
    toBlob,
    hasSignature: () => hasSignature,
  }));

  return (
    <div
      ref={containerRef}
      className={`signature-canvas-container ${className} ${disabled ? 'disabled' : ''}`}
    >
      <canvas
        ref={canvasRef}
        className="signature-canvas"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        onTouchCancel={stopDrawing}
      />
      {!hasSignature && !disabled && (
        <div className="signature-placeholder">
          Firme aqui
        </div>
      )}
    </div>
  );
});

SignatureCanvas.displayName = 'SignatureCanvas';

SignatureCanvas.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  lineWidth: PropTypes.number,
  lineColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  className: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

export default SignatureCanvas;
